import { Check, ChevronsUpDown, X } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@/components/ui/command";
import { Label } from "@/components/ui/label";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

/**
 * The three KPI slots, in the order the work happens: who made the Draft, who was asked
 * to carry it on, who was asked to turn it into an Order. See issue #131.
 *
 * The keys are the ones the server reads out of form_data.additionalSettings, so the
 * booking form can spread these straight into its values.
 */
export const PIC_SLOT_KEYS = ["picL1", "picL2", "picL3"] as const;
export type PicSlotKey = (typeof PIC_SLOT_KEYS)[number];
export type PicSlots = Record<PicSlotKey, string | null>;

export const EMPTY_PIC_SLOTS: PicSlots = {
	picL1: null,
	picL2: null,
	picL3: null,
};

/**
 * The role labels the server puts on each PIC — the wire half of
 * AppointmentDraft::PIC_SLOT_LABELS and the "Other" fallback in #pic_roles. Named here so
 * the strings live in one place on this side too; change either end and both must move.
 */
export const PIC_ROLE_LABELS = ["L1", "L2", "L3"] as const;
export const OTHER_PIC_ROLE = "Other";

/**
 * Sort key that puts the badges in the order the work happens, with everything the server
 * did not label ("Other") at the end.
 */
export const picRoleRank = (roles?: string[]): number => {
	const index = PIC_ROLE_LABELS.findIndex((label) => roles?.includes(label));
	return index === -1 ? PIC_ROLE_LABELS.length : index;
};

/**
 * Whether this admin already holds one of the three slots. Every control that shows the
 * supporting PICs asks it, because a slot holder must never appear there too: the two
 * lists are one union underneath, so a chip with a remove button that the next save
 * merges straight back in is a lie the user cannot see through.
 *
 * `slotIds` rather than PicSlots, so callers watching three separate form fields can pass
 * what they already have.
 */
export const holdsPicSlot = (
	slotIds: Array<string | null | undefined>,
	adminId: string | number,
): boolean => slotIds.some((id) => !!id && String(id) === String(adminId));

/**
 * The stage badge — "L1", "L1/L3" for someone who both made the Draft and submitted it,
 * or "Other" for a supporting PIC. Renders nothing when the server sent no roles, which
 * is every surface with DRAFT_PIC_KPI off or no Draft behind it: an empty badge would
 * claim a stage nobody holds.
 *
 * Lives beside picRoleRank so the badge and the ordering read the same vocabulary.
 */
export function PicRoleBadge({
	roles,
	className,
}: {
	roles?: string[];
	className?: string;
}) {
	if (!roles?.length) return null;

	return (
		<Badge variant="accent" className={cn("text-xs font-normal", className)}>
			{roles.join("/")}
		</Badge>
	);
}

export interface PicSlotAdmin {
	id: string;
	name: string;
	email?: string | null;
}

/**
 * The trigger's own skin, which has to match whatever control these selectors sit next to
 * — they always sit above a PIC picker, and two different pickers are in play. This is the
 * shape MultiSelectorTrigger wears (the WhatsApp template page, and the Drafts dialog,
 * which is on the same plain background); the booking form's own picker is tinted, so it
 * passes its own.
 */
const DEFAULT_TRIGGER_CLASS = "bg-background shadow ring-1 ring-muted";

function AdminSlotSelect({
	label,
	description,
	admins,
	value,
	disabled,
	clearable = true,
	triggerClassName,
	onChange,
}: {
	label: string;
	description: string;
	admins: PicSlotAdmin[];
	value: string | null;
	disabled?: boolean;
	// L1 passes false. It can be handed to somebody else but not emptied — every write
	// path restores a stored L1 rather than clearing it, so an × here would clear L2 and
	// L3 along with it and then watch L1 come back on save.
	clearable?: boolean;
	triggerClassName?: string;
	onChange: (value: string | null) => void;
}) {
	const { t } = useTranslation("appointments-form");
	const [open, setOpen] = useState(false);
	const selected = admins.find((admin) => String(admin.id) === String(value));

	return (
		<div className="flex flex-col gap-1.5">
			{/* Default size, uppercase: the same shape the PIC picker's own label wears on
			    both pages these sit on. */}
			<Label className="uppercase">{label}</Label>

			{/* modal, because two of the three surfaces are dialogs. The content is portalled
			    to the body, outside the dialog element, and the dialog's react-remove-scroll
			    refuses wheel events there — so the admin list rendered its scrollbar and then
			    ignored the wheel. A modal Popover brings its own scroll lock scoped to its own
			    content, which is what lets it through. */}
			<Popover modal open={open} onOpenChange={setOpen}>
				<PopoverTrigger asChild>
					<Button
						type="button"
						variant="outline"
						aria-expanded={open}
						disabled={disabled}
						className={cn(
							"w-full justify-between font-normal",
							triggerClassName || DEFAULT_TRIGGER_CLASS,
							!selected && "text-muted-foreground",
						)}
					>
						<span className="truncate">
							{selected?.name ||
								t("draft.pic_slots.placeholder", {
									defaultValue: "Belum dipilih",
								})}
						</span>
						<span className="flex items-center gap-1 shrink-0">
							{selected && !disabled && clearable && (
								// biome-ignore lint/a11y/useSemanticElements: nested inside the trigger button
								<span
									role="button"
									tabIndex={0}
									aria-label={t("draft.pic_slots.clear", {
										defaultValue: "Kosongkan",
									})}
									className="rounded p-0.5 opacity-70 hover:opacity-100 hover:text-destructive"
									onKeyDown={() => {}}
									onClick={(event) => {
										event.preventDefault();
										event.stopPropagation();
										onChange(null);
									}}
								>
									<X className="size-3.5" />
								</span>
							)}
							<ChevronsUpDown className="size-4 opacity-50" />
						</span>
					</Button>
				</PopoverTrigger>

				<PopoverContent
					className="p-0 w-[var(--radix-popover-trigger-width)]"
					align="start"
				>
					<Command>
						<CommandInput
							placeholder={t("draft.admin_pic.search", {
								defaultValue: "Cari admin...",
							})}
						/>
						<CommandList>
							<CommandEmpty>
								{t("draft.admin_pic.empty", {
									defaultValue: "Admin tidak ditemukan.",
								})}
							</CommandEmpty>
							<CommandGroup>
								{admins.map((admin) => (
									<CommandItem
										key={admin.id}
										value={admin.name}
										onSelect={() => {
											// Re-picking the one already chosen clears the slot,
											// except where clearing is not on offer at all — there it
											// is a no-op rather than a second way round the rule.
											const same = String(admin.id) === String(value);
											onChange(same && clearable ? null : String(admin.id));
											setOpen(false);
										}}
									>
										<div className="flex flex-col flex-1 min-w-0">
											<span className="text-sm truncate">{admin.name}</span>
											{admin.email && (
												<span className="text-xs truncate text-muted-foreground">
													{admin.email}
												</span>
											)}
										</div>
										<Check
											className={cn(
												"size-4",
												String(admin.id) === String(value)
													? "opacity-100"
													: "opacity-0",
											)}
										/>
									</CommandItem>
								))}
							</CommandGroup>
						</CommandList>
					</Command>
				</PopoverContent>
			</Popover>

			<p className="text-xs text-muted-foreground">{description}</p>
		</div>
	);
}

/**
 * Controlled on purpose: the same three selectors sit in the booking form (react-hook-form
 * state), on the WhatsApp template page (plain state) and in the Drafts "Ubah PIC" dialog,
 * and only the caller knows where the answer belongs.
 *
 * L2 and L3 are disabled without an L1 — the server rejects that arrangement, and a field
 * that silently fails to save is worse than one that says why it cannot be used yet.
 */
export function PicSlotSelectors({
	admins,
	value,
	onChange,
	disabled = false,
	className,
	triggerClassName,
}: {
	admins: PicSlotAdmin[];
	value: PicSlots;
	onChange: (slots: PicSlots) => void;
	disabled?: boolean;
	className?: string;
	// See DEFAULT_TRIGGER_CLASS: only pass this where the neighbouring PIC picker is
	// skinned differently, so the two never sit side by side in different colours.
	triggerClassName?: string;
}) {
	const { t } = useTranslation("appointments-form");
	const noL1 = !value.picL1;

	// Labels follow the columns they write — created_by, continued_by, submitted_by — so
	// the three read as one sequence rather than three unrelated job titles. "Disubmit"
	// keeps the word the admins already use for the act; "Penutup" invented a role nobody
	// says out loud.
	const slots: Array<{ key: PicSlotKey; label: string; description: string }> =
		[
			{
				key: "picL1",
				label: t("draft.pic_slots.l1.label", {
					defaultValue: "L1 — Dibuat oleh",
				}),
				description: t("draft.pic_slots.l1.description", {
					defaultValue: "Admin yang membuat draf ini.",
				}),
			},
			{
				key: "picL2",
				label: t("draft.pic_slots.l2.label", {
					defaultValue: "L2 — Dilanjutkan oleh",
				}),
				description: noL1
					? t("draft.pic_slots.needs_l1", {
							defaultValue: "Isi L1 dulu sebelum menugaskan L2 atau L3.",
						})
					: t("draft.pic_slots.l2.description", {
							defaultValue: "Admin yang ditugaskan melanjutkan draf ini.",
						}),
			},
			{
				key: "picL3",
				label: t("draft.pic_slots.l3.label", {
					defaultValue: "L3 — Disubmit oleh",
				}),
				description: noL1
					? t("draft.pic_slots.needs_l1", {
							defaultValue: "Isi L1 dulu sebelum menugaskan L2 atau L3.",
						})
					: t("draft.pic_slots.l3.description", {
							defaultValue:
								"Admin yang ditugaskan men-submit draf ini jadi order. Boleh orang yang sama dengan L1.",
						}),
			},
		];

	return (
		<div className={cn("grid gap-3 md:grid-cols-3", className)}>
			{slots.map((slot) => (
				<AdminSlotSelect
					key={slot.key}
					label={slot.label}
					description={slot.description}
					admins={admins}
					value={value[slot.key]}
					triggerClassName={triggerClassName}
					// L1 may be handed over, never emptied — see AdminSlotSelect. Setting one
					// on a Draft that has none is still open, which is what a customer-created
					// Draft needs.
					clearable={slot.key !== "picL1"}
					disabled={disabled || (noL1 && slot.key !== "picL1")}
					// No cascade needed on L1: it cannot be cleared here, so the "nothing may
					// be assigned onward on a Draft nobody started" rule can never be broken
					// from this control. It is enforced for every other caller by
					// AppointmentDraft#pic_slots_need_l1.
					onChange={(next) => onChange({ ...value, [slot.key]: next })}
				/>
			))}
		</div>
	);
}

export default PicSlotSelectors;
